import {
  autoUpdate,
  flip,
  offset,
  size,
  useFloating,
} from "@floating-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import { cn } from "../../lib/utils";

interface FormMeta {
  error?: string;
  touched?: boolean;
  value?: string;
}

interface FormHelpers {
  setTouched: (touched: boolean) => void;
  setValue: (value: any) => void;
}

interface TMySelectProps<T> extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "onChange" | "value" | "defaultValue"
> {
  getValue: (option: T) => any;
  getName: (option: T) => any;
  onValChange?: (option: T) => void;
  options: T[];
  name: string;
  defaultName?: string;
  label?: string;
  noPlaceholder?: boolean;
  refresh?: boolean;
  meta?: FormMeta;
  helpers?: FormHelpers;
  value?: string;
  onChange?: (value: string) => void;
  containerClassName?: string;
  dropdownClassName?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  showSearch?: boolean;
}

export function MySelect<T>(props: TMySelectProps<T>) {
  const {
    getValue,
    getName,
    options,
    defaultName,
    label,
    noPlaceholder,
    refresh,
    meta,
    helpers,
    value: controlledValue,
    onChange,
    onValChange,
    disabled,
    placeholder,
    id,
    containerClassName,
    dropdownClassName,
    className,
    showSearch = false,
  } = props;

  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Floating UI setup with smart positioning
  const { refs, floatingStyles, placement } = useFloating({
    open: isDropdownVisible,
    onOpenChange: setDropdownVisible,
    middleware: [
      offset(4), // 4px gap between trigger and dropdown
      flip({
        fallbackPlacements: ["top", "bottom"],
        padding: 8,
      }),
      size({
        apply({ availableHeight, elements }) {
          const searchOffset = showSearch ? 56 : 0;
          const maxHeight = Math.min(availableHeight - 16, 360 + searchOffset);
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`,
          });
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
  });

  // Determine if we're in form mode or controlled mode
  const isFormMode = meta && helpers;
  const currentValue = isFormMode ? meta.value : controlledValue;
  const isError = isFormMode ? meta.error && meta.touched : false;

  useClickOutside(wrapperRef, () => {
    setDropdownVisible(false);
    setSearchTerm("");
  });

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        getName(option).toLowerCase().includes(searchTerm.toLowerCase().trim()),
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, getName]);

  // Handle value changes
  const handleValueChange = useCallback(
    (newValue: string, option: T) => {
      if (isFormMode && helpers) {
        helpers.setTouched(true);
        helpers.setValue(newValue);
      }

      if (onChange) {
        onChange(newValue);
      }

      setSelectedOption(option);
      onValChange?.(option);
    },
    [isFormMode, helpers, onChange, onValChange],
  );

  const handleOptionClick = (e: React.MouseEvent, option: T) => {
    e.preventDefault();
    e.stopPropagation();

    const value = getValue(option);
    handleValueChange(value, option);
    setDropdownVisible(false);
    setSearchTerm("");
  };

  const toggleVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    const newVisibility = !isDropdownVisible;
    setDropdownVisible(newVisibility);

    if (newVisibility) {
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchTerm("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    switch (e.key) {
      case "Escape":
        setDropdownVisible(false);
        setSearchTerm("");
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions.length > 0) {
          const firstOption = filteredOptions[0];
          const value = getValue(firstOption);
          handleValueChange(value, firstOption);
          setDropdownVisible(false);
          setSearchTerm("");
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        const firstOption = dropdownRef.current?.querySelector(
          "[data-option]",
        ) as HTMLElement;
        firstOption?.focus();
        break;
    }
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    option: T,
    index: number,
  ) => {
    e.stopPropagation();

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        const value = getValue(option);
        handleValueChange(value, option);
        setDropdownVisible(false);
        setSearchTerm("");
        break;
      case "ArrowDown":
        e.preventDefault();
        const nextOption = dropdownRef.current?.querySelector(
          `[data-option-index="${index + 1}"]`,
        ) as HTMLElement;
        if (nextOption) {
          nextOption.focus();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (index === 0 && showSearch) {
          searchInputRef.current?.focus();
        } else {
          const prevOption = dropdownRef.current?.querySelector(
            `[data-option-index="${index - 1}"]`,
          ) as HTMLElement;
          prevOption?.focus();
        }
        break;
      case "Escape":
        setDropdownVisible(false);
        setSearchTerm("");
        break;
    }
  };

  // Handle refresh prop
  useEffect(() => {
    if (refresh) {
      setSelectedOption(null);
      setSearchTerm("");
      if (isFormMode && helpers) {
        helpers.setValue("");
      }
    }
  }, [refresh, isFormMode, helpers]);

  // Sync selected option with current value
  useEffect(() => {
    if (currentValue) {
      const foundOption = options.find(
        (option) => getValue(option) === currentValue,
      );
      if (foundOption && foundOption !== selectedOption) {
        setSelectedOption(foundOption);
      }
    } else if (currentValue === "" || currentValue === undefined) {
      setSelectedOption(null);
    }
  }, [currentValue, options, getValue, selectedOption]);

  // Get display text
  const getDisplayText = () => {
    if (selectedOption) {
      return getName(selectedOption);
    }
    return defaultName || placeholder || "Select an option...";
  };

  // Determine animation classes based on placement
  const getAnimationClasses = () => {
    const isTop = placement.startsWith("top");
    return cn(
      "animate-in fade-in-0 zoom-in-95",
      isTop ? "slide-in-from-bottom-2" : "slide-in-from-top-2",
    );
  };

  return (
    <div className={cn("relative", containerClassName)} title={placeholder}>
      {label && (
        <label htmlFor={id} className="block text-[13px] mb-3 font-semibold">
          {label}
        </label>
      )}

      <div className="relative" ref={wrapperRef}>
        <div
          ref={refs.setReference}
          className={cn(
            "h-[50px] w-full cursor-pointer py-1 px-4 text-sm flex items-center border rounded-md transition-colors",
            {
              // "bg-gray-300 opacity-25 cursor-not-allowed":
              //   disabled,
              "border-red-500": isError,
              "border-blue-500": isDropdownVisible && !isError,
              "border-gray-300 hover:border-gray-400":
                !isDropdownVisible && !isError && !disabled,
            },
            className,
          )}
          onClick={toggleVisibility}
          aria-expanded={isDropdownVisible}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-label={label || "Select option"}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleVisibility(e as any);
            }
          }}
        >
          {!noPlaceholder && (
            <span
              className={cn("flex-1 truncate", {
                "text-gray-400": !selectedOption,
              })}
            >
              {getDisplayText()}
            </span>
          )}

          {!disabled && (
            <svg
              className={cn(
                "ml-auto h-4 w-4 transition-transform flex-shrink-0",
                {
                  "rotate-180": isDropdownVisible,
                  "rotate-0": placement.startsWith("top") && isDropdownVisible,
                },
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  placement.startsWith("top") && isDropdownVisible
                    ? "M5 15l7-7 7 7"
                    : "M19 9l-7 7-7-7"
                }
              />
            </svg>
          )}
        </div>

        {/* Floating Dropdown */}
        {isDropdownVisible && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 9999,
              minWidth:
                refs.reference.current?.getBoundingClientRect().width || "auto",
            }}
            className={cn(
              "flex flex-col overflow-hidden rounded-md shadow-lg bg-white border border-gray-200",
              getAnimationClasses(),
              dropdownClassName,
            )}
            role="listbox"
            aria-label="Options"
            onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking dropdown
          >
            {/* Search Input */}
            {showSearch && (
              <div className="p-2 border-b border-gray-100 shrink-0 bg-white">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search options..."
                  aria-autocomplete="list"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  aria-label="Search options"
                />
              </div>
            )}

            {/* Options List */}
            <div
              ref={dropdownRef}
              className="overflow-y-auto overscroll-contain min-h-0 flex-1 bg-white"
            >
              {filteredOptions.length === 0 ? (
                <div className="py-4 px-5 text-gray-500 text-sm text-center">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getValue(option);
                  const isSelected =
                    selectedOption && getValue(selectedOption) === optionValue;

                  return (
                    <div
                      key={`${String(optionValue)}-${index}`}
                      data-option
                      data-option-index={index}
                      onClick={(e) => handleOptionClick(e, option)}
                      onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
                      className={cn(
                        "cursor-pointer py-2.5 px-5 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm transition-colors",
                        {
                          "bg-blue-50 text-blue-700": isSelected,
                        },
                      )}
                      role="option"
                      tabIndex={0}
                      aria-selected={!!isSelected}
                    >
                      {getName(option)}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {isError && meta?.error && (
        <p className="text-red-500 text-xs mt-1" role="alert">
          {meta.error}
        </p>
      )}
    </div>
  );
}
